import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import { IPermitTypeField } from '~/client/graph'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'

import {
  applyAllowance,
  denyOnRestrictedFields,
  IAllowanceEdit,
} from './resourceFieldAllowanceWrites'

/** Records whose WR toggle was just turned on, and the resource type they belong to. */
export interface IEnabledRecords {
  resourceTypeId: string
  recordIds: readonly string[]
}

/**
 * Applies allowance edits for one or more records and saves the types they actually moved.
 *
 * Permit types have no field-granular mutation - `saveMany` sends each type's whole
 * `workflowSteps` tree - but it takes a list, so however many types the edits span ride one
 * `SavePermitTypes` round trip. That is what lets a single Apply, or one bulk action over a
 * whole selection, be one write.
 *
 * Types are re-read from the store here rather than captured when the editor opened: the
 * payload is the entire type, so a stale copy would clobber someone else's concurrent edit
 * to an unrelated part of it.
 *
 * `enabled` names records whose WR toggle was turned on just before this save. The server
 * denied them on every restricted field in that write, and the store may not have heard yet,
 * so the copy is given the same denial before the edits land on it.
 */
function commitAllowanceEdits(
  workflowTypesStore: WorkflowTypesStore,
  recordIds: readonly string[],
  edits: IAllowanceEdit[],
  enabled?: IEnabledRecords,
): Promise<void> {
  const records = recordIds.filter(Boolean)
  if (!edits.length || !records.length) return Promise.resolve()

  const editsByType = new Map<string, Map<string, boolean>>()
  edits.forEach(edit => {
    const byField = editsByType.get(edit.typeId) ?? new Map<string, boolean>()
    byField.set(edit.fieldId, edit.shouldAllow)
    editsByType.set(edit.typeId, byField)
  })

  const changed: WorkflowType[] = []

  editsByType.forEach((byField, typeId) => {
    // Resolves a superseded id to the current version: writing a permit type can mint a
    // new one, so the id the caller listed may already be a previous version.
    const live = workflowTypesStore.getActualTypeByAnyVersionId(typeId)
    if (!live) return

    // A copy, never the store's own model: `saveMany` mutates the steps it is handed
    // (it strips each action's discriminator), so passing live state would corrupt it.
    const type = live.getDeepCopy()
    let isTouched = false

    if (enabled) {
      type.workflowSteps = denyOnRestrictedFields(
        type.workflowSteps,
        enabled.resourceTypeId,
        enabled.recordIds,
      )
    }

    const rewrite = (field: IPermitTypeField) => {
      if (!field?.id) return

      const shouldAllow = byField.get(String(field.id))
      if (shouldAllow === undefined) return

      // Folded one record at a time into the same array, so a selection of many lands as
      // a single rewrite of this field rather than one per record.
      const next = records.reduce(
        (options, recordId) => applyAllowance(options, recordId, shouldAllow),
        field.filterOptions,
      )
      // applyAllowance hands back this exact value when nothing moved - including the
      // undefined an unrestricted field carries - so the reference check skips it and an
      // untouched type stays out of the save instead of being minted a new version.
      if (next === field.filterOptions) return

      field.filterOptions = next
      isTouched = true
    }

    ;(type.workflowSteps ?? []).forEach(step => {
      ;(step?.fields ?? []).forEach(rewrite)
      ;(step?.conditionalFields ?? []).forEach(conditional =>
        (conditional?.values ?? []).forEach(rewrite),
      )
    })

    if (isTouched) changed.push(type)
  })

  // Resolves only once SavePermitTypes lands, so a caller can hold its UI - an open
  // popover, a selection it is about to clear - until the write is real. Rejects on
  // failure rather than leaving the promise pending: the save event only calls back on
  // success, so without the error arm an awaiting caller would hang on a failed write.
  if (!changed.length) return Promise.resolve()

  return new Promise<void>((resolve, reject) => {
    workflowTypesStore.saveMany(
      changed,
      () => resolve(),
      error => reject(error),
    )
  })
}

/** Commits allowance edits for one fixed record. */
export function useSaveResourceFieldAllowances(
  recordId: string,
): (edits: IAllowanceEdit[], enabled?: IEnabledRecords) => Promise<void> {
  const { workflowTypesStore } = React.useContext(MobXProviderContext) as {
    workflowTypesStore: WorkflowTypesStore
  }

  return React.useCallback(
    (edits: IAllowanceEdit[], enabled?: IEnabledRecords) =>
      commitAllowanceEdits(workflowTypesStore, [recordId], edits, enabled),
    [workflowTypesStore, recordId],
  )
}

/**
 * Bulk REPLACE across a selection: every selected record takes the given answer for every
 * field the editor listed - allowed on the ticked ones, denied on the rest.
 *
 * Replace, not merge, because that is what the bulk bar has always meant - the editor opens
 * with nothing ticked and what you tick becomes the value, the same contract the permitted
 * companies action uses. The cost is that it writes a deny for every field you did NOT tick,
 * for every record in the selection, from a screen that cannot show you what those fields
 * held. It is the one action here that can revoke configuration you never looked at.
 *
 * The edits come from the editor, built against the rows the user actually saw. They are
 * deliberately NOT recomputed from `actualTypes` here: a row id embeds the workflow type's
 * VERSION id, and saving a permit type mints a new one, so a type versioned while the editor
 * was open (by another admin, or between Apply and this call) would no longer match the
 * user's ticks - and every field they ticked on it would be written as a deny. Carrying the
 * edits keeps that impossible; commitAllowanceEdits resolves each typeId through
 * getActualTypeByAnyVersionId, so a superseded id still lands on the current version.
 *
 * Fields already in the requested state are left alone, so an unrestricted field that stays
 * allowed costs nothing and its workflow type is not rewritten.
 */
export function useBulkReplaceResourceFieldAllowances(): (
  recordIds: readonly string[],
  edits: IAllowanceEdit[],
  enabled?: IEnabledRecords,
) => Promise<void> {
  const { workflowTypesStore } = React.useContext(MobXProviderContext) as {
    workflowTypesStore: WorkflowTypesStore
  }

  return React.useCallback(
    (
      recordIds: readonly string[],
      edits: IAllowanceEdit[],
      enabled?: IEnabledRecords,
    ) => commitAllowanceEdits(workflowTypesStore, recordIds, edits, enabled),
    [workflowTypesStore],
  )
}
